import React, {Fragment, useEffect, useState} from "react";
import PropTypes from "prop-types";
import {Card, Col, Form, Row, Toast} from "react-bootstrap";
import {Button} from "../../components/buttons";
import {saveUserProfileInfo, useGetUserProfileInfo,} from "../../actions/accountSettingsAction";
import {Filter} from "../../components/filters/filterGroup";
import {FILTER_DEFINITIONS_MAP} from "../../components/filters/filterDefinition";
import {EQHSpinner} from "../../components/EQHSpinner";
import {FilterParam} from "../../dtos/PatientSearchDTO";
import {stateAbbrMap} from "../../actions/constantsActions"
import {handlePkgErrors} from "../../utils/util";

const componentName = "PersonalInfomration";

const userProfileReducer = (state, action) => {
  if (action.type === "Update") {
    return { ...state, data: action.payload };
  }

  throw new Error(`Unsupported action type: ${action.type}`);
};

const getUserProfileValue = (userProfileInfo, fieldName) => userProfileInfo?.[fieldName] ?? "";

const omitNpi = (userProfileInfo) => {
  const { npi, ...userProfileInfoWithoutNpi } = userProfileInfo || {};
  return userProfileInfoWithoutNpi;
};

const dispatchUserProfileUpdate = (dispatch, currentData, fieldName, value) => {
  dispatch({
    type: "Update",
    payload: {
      ...currentData,
      [fieldName]: value,
    },
  });
};

const getStateFilterParam = (userProfileInfo) => {
  if (!userProfileInfo?.state) {
    return null;
  }

  return new FilterParam(
    "State",
    "eq",
    [userProfileInfo.state],
    (e) => e,
    (e) => stateAbbrMap[userProfileInfo.state]
  );
};

const UserProfileTextField = ({
  autoComplete,
  className = "form-control",
  dispatch,
  fieldName,
  idPrefix = fieldName,
  label,
  name = fieldName,
  userProfileInfo,
}) => {
  const inputId = `${componentName}-${idPrefix}Value`;

  return (
    <Fragment>
      <label className="d-block" htmlFor={inputId} id={`${componentName}-${idPrefix}Label`}>
        {label}
      </label>
      <input
        type="text"
        id={inputId}
        name={name}
        className={className}
        autoComplete={autoComplete}
        value={getUserProfileValue(userProfileInfo, fieldName)}
        onChange={(e) => dispatchUserProfileUpdate(dispatch, userProfileInfo, fieldName, e.target.value)}
      />
    </Fragment>
  );
};

UserProfileTextField.propTypes = {
  autoComplete: PropTypes.string,
  className: PropTypes.string,
  dispatch: PropTypes.func.isRequired,
  fieldName: PropTypes.string.isRequired,
  idPrefix: PropTypes.string,
  label: PropTypes.string.isRequired,
  name: PropTypes.string,
  userProfileInfo: PropTypes.object,
};

const PersonalInformation = () => {
  const [show, setShow] = useState(false);
  const userProfilePkg = useGetUserProfileInfo({}, userProfileReducer);
  const userProfileInfo = userProfilePkg.state.data;

  const save = (userProfileInfo) => {
    saveUserProfileInfo(omitNpi(userProfileInfo))
      .then(() => {
        setShow(true);
      })
      .catch(() => {
        setShow(false);
      });
  };

  useEffect(() => {
    userProfilePkg.fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Fragment>
      {userProfilePkg.state.isLoading && <EQHSpinner />}
      {userProfilePkg.state.error ? (
        <Card>
          <Card.Body>{handlePkgErrors(userProfilePkg.state.error)}</Card.Body>
        </Card>
      ) : (
        <Card>
          <Card.Body>
            <Form autoComplete="off">
            <Row>
              <Col sm={4} className="mb-3">
                <UserProfileTextField
                  className="form-control text-capitalize"
                  dispatch={userProfilePkg.dispatch}
                  fieldName="firstName"
                  label="First Name"
                  userProfileInfo={userProfileInfo}
                />
              </Col>
              <Col sm={4} className="mb-3">
                <UserProfileTextField
                  className="form-control text-capitalize"
                  dispatch={userProfilePkg.dispatch}
                  fieldName="middleName"
                  label="Middle Name"
                  userProfileInfo={userProfileInfo}
                />
              </Col>
              <Col sm={4} className="mb-3">
                <UserProfileTextField
                  className="form-control text-capitalize"
                  dispatch={userProfilePkg.dispatch}
                  fieldName="lastName"
                  label="Last Name"
                  userProfileInfo={userProfileInfo}
                />
              </Col>
            </Row>
            <hr />
            <Row>
              <Col sm={4} className="mb-3">
                <UserProfileTextField
                  className="form-control text-capitalize"
                  dispatch={userProfilePkg.dispatch}
                  fieldName="address"
                  label="Address"
                  userProfileInfo={userProfileInfo}
                />
              </Col>
              <Col sm={4} className="mb-3">
                <UserProfileTextField
                  dispatch={userProfilePkg.dispatch}
                  fieldName="primaryPhone"
                  label="Primary Phone"
                  userProfileInfo={userProfileInfo}
                />
              </Col>
              <Col sm={4} className="mb-3">
                <UserProfileTextField
                  dispatch={userProfilePkg.dispatch}
                  fieldName="email"
                  label="Email"
                  userProfileInfo={userProfileInfo}
                />
              </Col>
            </Row>
            <Row>
              <Col sm={4} className="mb-3">
                <UserProfileTextField
                  className="form-control text-capitalize"
                  dispatch={userProfilePkg.dispatch}
                  fieldName="city"
                  label="City"
                  userProfileInfo={userProfileInfo}
                />
              </Col>
              <Col sm={4} className="mb-3">
                <UserProfileTextField
                  dispatch={userProfilePkg.dispatch}
                  fieldName="secondaryPhone"
                  idPrefix="altPhone"
                  label="Alt Phone"
                  name="altPhone"
                  userProfileInfo={userProfileInfo}
                />
              </Col>
              <Col sm={4} className="mb-3">
                <UserProfileTextField
                  dispatch={userProfilePkg.dispatch}
                  fieldName="federalDea"
                  label="Federal Dea #"
                  userProfileInfo={userProfileInfo}
                />
              </Col>
            </Row>
            <Row>
              <Col sm={4}>
                <Row>
                  <Col sm={6} className="mb-3">
                    <Filter
                      filterDefinition={
                        FILTER_DEFINITIONS_MAP["State_1_Select"]
                      }
                      name="state"
                      selected={getStateFilterParam(userProfileInfo)}
                      handleChange={(value) =>
                        dispatchUserProfileUpdate(
                          userProfilePkg.dispatch,
                          userProfileInfo,
                          "state",
                          value.paramValue[0]
                        )
                      }
                      componentProps={{
                        showClearButton: false,
                        highlightWhenHasValue: false,
                      }}
                    />
                  </Col>
                  <Col sm={6} className="mb-3">
                    <UserProfileTextField
                      dispatch={userProfilePkg.dispatch}
                      fieldName="zip"
                      label="Zip"
                      userProfileInfo={userProfileInfo}
                    />
                  </Col>
                </Row>
              </Col>
              <Col sm={4} className="mb-3">
                <UserProfileTextField
                  dispatch={userProfilePkg.dispatch}
                  fieldName="fax"
                  label="Fax"
                  userProfileInfo={userProfileInfo}
                />
              </Col>
            </Row>
            <Row>
              <Col sm={2}>
                <Button id={`${componentName}-saveButton`} variant="primary" onClick={() => save(userProfileInfo)}>
                  Save
                </Button>
              </Col>
              <Col sm={10}>
                <Toast
                  onClose={() => setShow(false)}
                  show={show}
                  delay={5000}
                  autohide
                >
                  <Toast.Header>
                    <i className="fas fa-check-square text-success"></i>
                    <strong className="me-auto">
                      Personal information updated
                    </strong>
                  </Toast.Header>
                </Toast>
              </Col>
            </Row>
            </Form>
          </Card.Body>
        </Card>
      )}
    </Fragment>
  );
};

export default PersonalInformation;
